import { useCallback, useMemo } from 'react';
import { Platform } from 'react-native';

import {
  type PermissionsMachineImplementations,
  useInitPermissionsMachine,
  usePermissionsMachine,
} from '@sg/shared-fe-features-permissions';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

export const useBackgroundLocationPermissionPrimer = () => {
  const { track } = useTelemetry();
  const isFeatureEnabled = useFeatureFlag(
    'CELS-5610-order-background-location-permission-primer-enabled',
  );

  // ─── Derived ─────────────────────────────────────────────────────────

  const isBackgroundLocationPrimerEnabled = Platform.OS !== 'web' && isFeatureEnabled;

  // ─── Actions ─────────────────────────────────────────────────────────

  const onBackgroundLocationPrimerShow = useCallback(() => {
    track('background_location_primer_shown', BACKGROUND_LOCATION_PRIMER_TELEMETRY_PAYLOAD);
  }, [track]);

  const onSkipBackgroundLocationPermission = useCallback(() => {
    track('background_location_primer_dismissed', {
      ...BACKGROUND_LOCATION_PRIMER_TELEMETRY_PAYLOAD,
      action: 'not_now',
    });
  }, [track]);

  const onBackgroundLocationUpgradeDialogShow = useCallback(() => {
    track('background_location_upgrade_dialog_shown', BACKGROUND_LOCATION_PRIMER_TELEMETRY_PAYLOAD);
  }, [track]);

  const onBackgroundLocationPermissionGranted = useCallback(() => {
    track('background_location_permission_granted', BACKGROUND_LOCATION_PRIMER_TELEMETRY_PAYLOAD);
  }, [track]);

  const onBackgroundLocationPermissionDenied = useCallback(() => {
    track('background_location_permission_denied', BACKGROUND_LOCATION_PRIMER_TELEMETRY_PAYLOAD);
  }, [track]);

  // ─────────────────────────────────────────────────────────────────────

  const implementations = useMemo<PermissionsMachineImplementations>(
    () => ({
      actions: {
        onBackgroundLocationPrimerShow,
        onSkipBackgroundLocationPermission,
        onBackgroundLocationUpgradeDialogShow,
        onBackgroundLocationPermissionGranted,
        onBackgroundLocationPermissionDenied,
      },
    }),
    [
      onBackgroundLocationPermissionDenied,
      onBackgroundLocationPermissionGranted,
      onBackgroundLocationPrimerShow,
      onBackgroundLocationUpgradeDialogShow,
      onSkipBackgroundLocationPermission,
    ],
  );

  // ─── Actor ───────────────────────────────────────────────────────────

  const permissionsActorRef = useInitPermissionsMachine({
    isBackgroundLocationPrimerEnabled,
    implementations,
  });

  const {
    isBackgroundLocationPermissionsLoading,
    isBackgroundLocationPrimerVisible,
    isRequestingBackgroundLocationPermission,
    isSkippingBackgroundLocationPermission,
    isBackgroundLocationPermissionApproved,
    isBackgroundLocationPermissionDenied,
    isBackgroundLocationPermissionSkipped,
    isBackgroundLocationPermissionAlreadyShown,
    requestBackgroundLocationPermissions,
    skipBackgroundLocationPermissions,
  } = usePermissionsMachine(permissionsActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPrimerActive =
    isBackgroundLocationPrimerVisible ||
    isRequestingBackgroundLocationPermission ||
    isSkippingBackgroundLocationPermission;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isBackgroundLocationPrimerEnabled,
    isBackgroundLocationPermissionsLoading,
    isPrimerActive,
    isRequestingBackgroundLocationPermission,
    isSkippingBackgroundLocationPermission,
    isBackgroundLocationPermissionApproved,
    isBackgroundLocationPermissionDenied,
    isBackgroundLocationPermissionSkipped,
    isBackgroundLocationPermissionAlreadyShown,
    onAllow: requestBackgroundLocationPermissions,
    onSkip: skipBackgroundLocationPermissions,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const BACKGROUND_LOCATION_PRIMER_TELEMETRY_PAYLOAD = { entry_point: 'location-search' };
